// SPDX-License-Identifier: GPL-3.0-or-later
//
// Wallet integration mirroring the bmc-edit pattern. Single shared `wallet`
// object (the SDK's lazy proxy to window.injinaryWallet), plus a tiny store
// of connection state that the UI subscribes to.

import {
  createInjinaryWallet,
  isAvailable as walletIsAvailable,
  type Connection,
} from '@injinary-wallet/sdk';
import type { BudgetStatus, PermissionSummary } from '@injinary-wallet/shared';

export const wallet = createInjinaryWallet();

const APP_NAME = 'Applify';

// Shown when the wallet can't be reached. The usual cause is that the wallet
// was installed after this tab was opened, so its content script isn't injected
// here yet — a reload fixes it. (The SDK detects the wallet via a postMessage
// round-trip answered by that content script.)
export const WALLET_NOT_DETECTED =
  "Couldn't reach the Injinary Wallet.";

// The SDK's default availability probe is a single 500ms postMessage round-trip,
// which is easy to miss if the wallet's service worker is waking up. Retry a few
// times with a longer window before declaring it missing.
async function waitForWallet(attempts: number, timeoutMs: number): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    try {
      if (await walletIsAvailable(timeoutMs)) return true;
    } catch {
      /* keep trying */
    }
  }
  return false;
}
const REQUESTED_PROVIDERS: string[] = ['anthropic', 'openai'];
const REQUESTED_BUDGET = { limit: 2000, period: 'monthly' as const };
const WANT_CONNECTED_KEY = 'applify:wallet:wantConnected';

function setWantConnected(want: boolean): void {
  try {
    if (want) localStorage.setItem(WANT_CONNECTED_KEY, '1');
    else localStorage.removeItem(WANT_CONNECTED_KEY);
  } catch { /* private mode */ }
}

function getWantConnected(): boolean {
  try { return localStorage.getItem(WANT_CONNECTED_KEY) === '1'; }
  catch { return false; }
}

export interface WalletState {
  connection: Connection | null;
  permissions: PermissionSummary | null;
  budget: BudgetStatus | null;
  lastError: string | null;
  busy: boolean;
}

let state: WalletState = {
  connection: null,
  permissions: null,
  budget: null,
  lastError: null,
  busy: false,
};

type Listener = (s: WalletState) => void;
const listeners = new Set<Listener>();

export function subscribeWallet(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

function update(patch: Partial<WalletState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

export function getWalletState(): WalletState {
  return state;
}

// `silent` skips the install banner and surfacing of errors; used by the
// auto-reconnect path on app boot.
export async function connectWallet(opts: { silent?: boolean } = {}): Promise<boolean> {
  if (state.busy) return state.connection !== null;
  update({ busy: true, lastError: null });
  try {
    const available = await waitForWallet(opts.silent ? 2 : 3, 700);
    if (!available) {
      update({ busy: false, lastError: opts.silent ? null : WALLET_NOT_DETECTED });
      return false;
    }
    const conn = await wallet.connect({
      appName: APP_NAME,
      requestedProviders: REQUESTED_PROVIDERS,
      requestedBudget: REQUESTED_BUDGET,
    });
    // Stale-handler guard: when we reconnect, the previous connection's
    // 'disconnected' event must not clobber the new connection's state.
    conn.on('disconnected', () => {
      if (state.connection !== conn) return;
      update({ connection: null, permissions: null, budget: null });
      setWantConnected(false);
    });
    conn.on('budgetWarning', ({ remaining }) => {
      if (state.connection !== conn) return;
      update({ lastError: `AI budget low — ${remaining} remaining this period.` });
    });
    conn.on('permissionsChanged', (permissions) => {
      if (state.connection !== conn) return;
      update({ permissions });
    });
    let budget: BudgetStatus | null = null;
    try { budget = await conn.getBudget(); }
    catch { /* not all providers expose budget */ }
    update({ connection: conn, permissions: conn.permissions, budget, busy: false });
    setWantConnected(true);
    return true;
  } catch (err) {
    update({
      busy: false,
      lastError: opts.silent ? null : (err instanceof Error ? err.message : String(err)),
    });
    return false;
  }
}

export function disconnectWallet(): void {
  state.connection?.disconnect();
  setWantConnected(false);
  update({ connection: null, permissions: null, budget: null, lastError: null });
}

// Boot-time silent reconnect. Safe to call even when there's no prior session
// — it's a no-op when the flag isn't set.
export async function autoReconnectWallet(): Promise<void> {
  if (!getWantConnected()) return;
  await connectWallet({ silent: true });
}

export function requireConnection(): Connection {
  if (!state.connection) throw new Error('Wallet is not connected.');
  return state.connection;
}

// Heuristic for "the wallet locked itself in the middle of our session".
// The Injinary Wallet auto-locks on idle; subsequent `complete()` calls reject
// with a message like "Wallet is locked. Please unlock it first." The string
// is not part of a stable API surface so we match loosely.
export function isWalletLockedError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /lock(?:ed)?/i.test(msg);
}

// Re-establish a fresh connection. Used by ai/client when a `complete()` call
// reports the wallet locked. The wallet extension prompts the user to unlock
// during connect(); only after the user clicks through does this resolve.
export async function reconnectWallet(): Promise<boolean> {
  // Tear down the existing connection optimistically so connectWallet() runs
  // the full path (it's a no-op if state was already cleared).
  if (state.connection) {
    try { state.connection.disconnect(); }
    catch { /* may already be dead */ }
  }
  state = { ...state, connection: null, permissions: null, budget: null, busy: false };
  return connectWallet({ silent: false });
}
